import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GeneralDataService } from '../../../services/GeneralDataService';
import { Subscription, take} from 'rxjs';
import { LegalInformationRule } from '../../../models/legalInformationRule';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Property } from '../../../models/property';
import { BasicResponse } from '../../../responses/basicResponse';
import { StoreLegalInformationRuleResponse } from '../../../responses/storeLegalInformationRuleResponse';

@Component({ standalone: false,
    selector: 'app-legal-information-property-edit',
    templateUrl: './legal-information-property-edit.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./legal-information-property-edit.component.scss']
  })
  export class LegalInformationPropertyEditComponent {
    readonly property = input<Property>();
    readonly legalInformationTypeId = input<number>();
    readonly propertySpecificChanged = output<boolean>();

    public ruleId?:number;
    public isPropertySpecific:boolean=false;
    public busy:boolean=false;
    public editMode:boolean=false;
    public editForm: FormGroup;
    private originalText?:string;

    public existingLegalRuleId?:number;

    get canCustomize(){
      return (this.editMode==false && this.isPropertySpecific==false);
    }
    get canEdit(){
      return (this.editMode==false && this.isPropertySpecific==true);
    }
    get canDelete(){
      return (this.editMode==true && this.isPropertySpecific==true);
    }
    get canSave(){
      return this.editMode==true;
    }
    get canCancel(){
      return this.editMode==true;
    }
   
    private legalInformationRuleCacheChangedSubscription?:Subscription;

    constructor(private gdService:GeneralDataService, private snackBar: MatSnackBar){
      this.editForm = new FormGroup({
         text: new FormControl<string | undefined>(undefined, Validators.required)
      });
    }

    ngOnInit(){
      this.loadLegalText();
    }

    
    ngOnDestroy(){
      if(this.legalInformationRuleCacheChangedSubscription) this.legalInformationRuleCacheChangedSubscription.unsubscribe();
    }

    private loadLegalText(){
      //check if property has already a specific rule and if so, load the rule, other wise load the default text
      this.gdService.getLegalInformationRules().pipe(take(1)).subscribe(lirArray=>{
        let lir = lirArray.find(lir=>lir.propertyId==this.property()!.id && lir.typeId==this.legalInformationTypeId());
        if(!lir){
          //no specific rule for this property found. load default for pre-population
          let lir=new LegalInformationRule();
          lir.typeId=this.legalInformationTypeId()!;
          lir.stateCode=this.property()!.address.state;
          lir.saleTypeId=this.property()!.saleType?.id;
          lir.auctionType=this.property()!.auction?.auctionType;
          lir.propertyTypeId=this.property()!.type?.id;
          this.gdService.getLegalInformation(lir).pipe(take(1)).subscribe(result=>{
            this.originalText=result.message;
            this.editForm.get('text')!.setValue(result.message);
            this.ruleId=result.code;
            this.isPropertySpecific=(result.bigCode==this.property()!.id);
            this.propertySpecificChanged.emit(this.isPropertySpecific);
          });
        }
        else{
          this.existingLegalRuleId=lir.id;
          this.originalText=lir.text;
          this.editForm.get('text')!.setValue(lir.text);
          this.ruleId=lir.id;
          this.isPropertySpecific=(lir.propertyId==this.property()!.id);
          this.propertySpecificChanged.emit(this.isPropertySpecific);
        }
      });
     
    }

    public onCustomizeClick(){
      this.editMode=true;
    }

    public onDeleteClick(){
      //show spinner
      this.busy=true;
      let lir:LegalInformationRule=new LegalInformationRule();
      lir.id=this.existingLegalRuleId;
      this.gdService.deleteLegalInformationRule(lir).pipe(take(1)).subscribe(r=>this.processDeleteResponse(r));
    }

    private processDeleteResponse(r:BasicResponse){
      this.busy=false;
      if(r.success){
        this.loadLegalText();
        this.snackBar.open('The legal information rule for "' + this.property()!.header + '" has been deleted.', '×', {
          verticalPosition: 'top',
          duration: 5000 
        }); 
        this.editMode=false;
      }
      else{
        this.snackBar.open('Deleting legal information rule failed: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
    }

    public onSaveClick(){
      //show spinner
      this.busy=true;
      let lir=new LegalInformationRule();
      if(this.existingLegalRuleId) lir.id=this.existingLegalRuleId;
      lir.typeId=this.legalInformationTypeId()!;
      lir.name = 'PROPERTY ' + this.property()!.header;
      lir.text = this.editForm.get('text')!.value;
      lir.propertyId = this.property()!.id;
      this.gdService.storeLegalInformationRule(lir).pipe(take(1)).subscribe(r=>this.processStoreResponse(r));
    }

    private processStoreResponse(r:StoreLegalInformationRuleResponse){
      this.busy=false;
      if(r.success){
        this.existingLegalRuleId=r.legalInformationRule!.id;
        this.isPropertySpecific=true;
        this.propertySpecificChanged.emit(this.isPropertySpecific);
        this.snackBar.open('The legal information rule for "' + this.property()!.header + '" has been saved.', '×', {
          verticalPosition: 'top',
          duration: 5000 
        }); 
        this.editMode=false;
      }
      else{
        this.snackBar.open('Saving legal information rule failed: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
      }
     
    }

    public onCancelClick(){
      this.editMode=false;
      this.editForm.get('text')!.setValue(this.originalText);
    }

  }